import { useEffect, useState, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../shared/contexts/ThemeContext";
import { useToast, ConfirmModal } from "../../../shared/components/SharedUI";
import ModalPortal from "../../../shared/components/ModalPortal";
import { overlayClose } from "../../../shared/utils/overlayClose";
import {
  getAreas, getDisponibilidad, crearReservacion, getReservaciones, cancelarReservacion,
  getParametrosReservacion,
} from "../services/portalService";

const MESES = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];
const DIAS  = ["Dom","Lun","Mar","Mié","Jue","Vie","Sáb"];

/** "06:00:00" -> "06:00". La hora viene con segundos y nadie los necesita. */
function hhmm(hora) {
  return typeof hora === "string" ? hora.slice(0, 5) : "";
}

/** "2026-10-28" -> "miércoles 28 de octubre de 2026" */
function fechaLarga(iso) {
  if (!iso) return "";
  const [a, m, d] = iso.split("-").map(Number);
  const fecha = new Date(a, m - 1, d);
  const dias = ["domingo","lunes","martes","miércoles","jueves","viernes","sábado"];
  return `${dias[fecha.getDay()]} ${d} de ${MESES[m - 1].toLowerCase()} de ${a}`;
}

const ESTADO_COLORS = {
  PENDIENTE: { bg:"rgba(245,158,11,0.1)",  border:"rgba(245,158,11,0.3)",  text:"#f59e0b" },
  APROBADA:  { bg:"rgba(74,222,128,0.1)",  border:"rgba(74,222,128,0.3)",  text:"#4ade80" },
  RECHAZADA: { bg:"rgba(248,113,113,0.1)", border:"rgba(248,113,113,0.3)", text:"#f87171" },
  CANCELADA: { bg:"rgba(156,163,175,0.1)", border:"rgba(156,163,175,0.3)", text:"#9ca3af" },
};

function Badge({ estado }) {
  const c = ESTADO_COLORS[estado] || ESTADO_COLORS.PENDIENTE;
  return (
    <span style={{ background:c.bg, border:`1px solid ${c.border}`, color:c.text, borderRadius:20, padding:"3px 10px", fontSize:11, fontWeight:700 }}>
      {estado}
    </span>
  );
}

export default function PortalReservaciones() {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const isLight = theme === "light";
  const { showToast, toastEl } = useToast();

  const today = new Date();
  const [year,  setYear]  = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [areas,          setAreas]          = useState([]);
  const [selectedArea,   setSelectedArea]   = useState(null);
  const [disponibilidad, setDisp]           = useState([]);
  const [selectedDate,   setSelDate]        = useState(null);
  const [reservaciones,  setReservaciones]  = useState([]);
  const [tab,    setTab]    = useState("reservar");
  const [notas,       setNotas]       = useState("");
  const [comprobante, setComprobante] = useState(null);
  const [saving,      setSaving]      = useState(false);
  const [confirm,     setConfirm]     = useState(null); // { pk, name }
  // Horario que el residente tocó y todavía NO reservó. El botón decía
  // "Reservar" y reservaba de una: la gente creía que solo estaba escogiendo la
  // franja y quedaba con reservas que no quería. Ahora pasa por esta pantalla.
  const [porConfirmar, setPorConfirmar] = useState(null);
  const [canceling,   setCanceling]   = useState(false);
  // Reglas del condominio: hasta cuándo se puede reservar y si la filial está
  // bloqueada por mora (ver ParametrosReservacion en el backend).
  const [reglas,      setReglas]      = useState(null);
  const [verDetalles, setVerDetalles] = useState(false);  // reglamento del área, plegado
  const fileRef = useRef();

  const loadReservaciones = useCallback(() => {
    getReservaciones().then(d => setReservaciones(d.results || d)).catch(() => {});
  }, []);

  useEffect(() => {
    getAreas().then(d => {
      const activas = (d.results || d).filter(a => a.activa);
      setAreas(activas);
    }).catch(() => {});
    loadReservaciones();
    getParametrosReservacion().then(setReglas).catch(() => {});
  }, [loadReservaciones]);

  const loadDisp = useCallback(() => {
    if (!selectedArea) return;
    getDisponibilidad(selectedArea.id, year, month).then(setDisp).catch(() => {});
  }, [selectedArea, year, month]);

  useEffect(() => { loadDisp(); }, [loadDisp]);

  // Si el admin aprueba/rechaza una reserva mientras estamos en esta pantalla,
  // refrescamos la lista y la disponibilidad solos.
  useEffect(() => {
    function onDataChanged(e) {
      if (e.detail?.tipo === "RESERVA") { loadReservaciones(); loadDisp(); }
    }
    window.addEventListener("portal:data-changed", onDataChanged);
    return () => window.removeEventListener("portal:data-changed", onDataChanged);
  }, [loadReservaciones, loadDisp]);

  function isOcupado(date, horarioId) {
    return disponibilidad.some(d => d.fecha === date && d.horario === horarioId);
  }

  const firstDay    = new Date(year, month - 1, 1).getDay();
  const daysInMonth = new Date(year, month, 0).getDate();
  const todayStr    = `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,"0")}-${String(today.getDate()).padStart(2,"0")}`;
  // Tope de anticipación del condominio (null = sin tope).
  const fechaMaxima = reglas?.fecha_maxima || null;
  const bloqueado   = !!reglas?.mora?.bloqueada;

  function prevMonth() { if (month===1){setMonth(12);setYear(y=>y-1);}else setMonth(m=>m-1); setSelDate(null); }
  function nextMonth() { if (month===12){setMonth(1);setYear(y=>y+1);}else setMonth(m=>m+1); setSelDate(null); }
  function dateStr(d)  { return `${year}-${String(month).padStart(2,"0")}-${String(d).padStart(2,"0")}`; }

  async function handleReservar(horario) {
    if (!selectedDate) return;
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("area", selectedArea.id);
      fd.append("horario", horario.id);
      fd.append("fecha", selectedDate);
      fd.append("notas", notas);
      if (comprobante) fd.append("comprobante_pago", comprobante);
      await crearReservacion(fd);
      showToast(t("portal.reservaCreada"), true);
      setPorConfirmar(null);
      setNotas(""); setComprobante(null); loadDisp();
      loadReservaciones();
      getParametrosReservacion().then(setReglas).catch(() => {});
    } catch (e) {
      try { const d = JSON.parse(e.message); showToast(Object.values(d).flat().join(". "), false); }
      catch { showToast(e.message, false); }
    } finally { setSaving(false); }
  }

  async function handleCancelar() {
    if (!confirm) return;
    setCanceling(true);
    try {
      await cancelarReservacion(confirm.pk);
      showToast(t("portal.reservaCancelada"), true);
      setConfirm(null);
      loadReservaciones();
    } catch { showToast(t("portal.errorCancelar"), false); }
    finally { setCanceling(false); }
  }

  const horarios = selectedArea?.horarios?.filter(h => h.activo) || [];

  // Hero theme vars
  const heroBg   = isLight ? "linear-gradient(135deg,#f0fdf4 0%,#ecfdf5 50%,#f0fdff 100%)" : "linear-gradient(135deg,#0d1f18 0%,#0f172a 60%,#0d1a2d 100%)";
  const heroText  = isLight ? "#0f172a" : "#ffffff";
  const heroSub   = isLight ? "rgba(15,23,42,0.5)" : "rgba(255,255,255,0.45)";
  const heroBorder= isLight ? "rgba(74,222,128,0.3)" : "rgba(74,222,128,0.18)";
  const heroShadow= isLight ? "0 4px 24px rgba(74,222,128,0.15),0 1px 3px rgba(0,0,0,0.06)" : "0 0 0 1px rgba(255,255,255,0.03),0 16px 48px rgba(0,0,0,.5)";

  return (
    <>
      {toastEl}
      <ConfirmModal
        open={!!confirm}
        name={confirm?.name}
        message={<>{t("portal.confirmarCancelar")} <strong style={{color:"#f87171"}}>{confirm?.name}</strong>?</>}
        onConfirm={handleCancelar}
        onCancel={() => setConfirm(null)}
        loading={canceling}
      />

      {/* ── Confirmar la reserva ── */}
      {porConfirmar && (
        <ModalPortal>
          <div {...overlayClose(() => !saving && setPorConfirmar(null))} style={{
            position:"fixed", inset:0, background:"rgba(0,0,0,0.65)",
            backdropFilter:"blur(3px)", zIndex:1000,
            display:"flex", alignItems:"center", justifyContent:"center", padding:16,
          }}>
            <div onClick={e => e.stopPropagation()} style={{
              background:"var(--bg-card)", border:"1px solid var(--border-card)",
              borderRadius:14, width:"min(420px,96vw)", zIndex:1001,
              boxShadow:"0 24px 64px rgba(0,0,0,0.5)", overflow:"hidden",
            }}>
              <div style={{ padding:"20px 22px 16px", textAlign:"center" }}>
                <div style={{
                  width:46, height:46, borderRadius:13, margin:"0 auto 12px",
                  background:"rgba(249,115,22,0.12)", border:"1px solid rgba(249,115,22,0.3)",
                  display:"flex", alignItems:"center", justifyContent:"center",
                }}>
                  <i className="fa-solid fa-calendar-check" style={{ color:"#f97316", fontSize:19 }} />
                </div>
                <h3 style={{ margin:"0 0 4px", fontSize:17, fontWeight:800, color:"var(--text-primary)" }}>
                  ¿Confirmás esta reserva?
                </h3>
                <p style={{ margin:0, fontSize:12.5, color:"var(--text-secondary)", lineHeight:1.5 }}>
                  Revisá que esté bien antes de enviarla.
                </p>
              </div>

              <div style={{ padding:"0 22px" }}>
                <div style={{
                  border:"1px solid var(--border-card)", borderRadius:10,
                  background:"var(--hover-bg)", padding:"4px 14px",
                }}>
                  {[
                    ["Área",    selectedArea?.nombre],
                    ["Fecha",   fechaLarga(selectedDate)],
                    ["Horario", `${porConfirmar.nombre} (${hhmm(porConfirmar.hora_inicio)} a ${hhmm(porConfirmar.hora_fin)})`],
                    ["Costo",   selectedArea?.precio_colones
                        ? `₡${Number(selectedArea.precio_colones).toLocaleString("en-US")}`
                        : selectedArea?.precio_dolares
                        ? `$${Number(selectedArea.precio_dolares).toLocaleString("en-US", { minimumFractionDigits:2 })}`
                        : "Sin costo"],
                    ["Notas",   notas],
                    ["Comprobante", comprobante?.name],
                  ].filter(([, v]) => v).map(([et, v], i) => (
                    <div key={et} style={{
                      display:"flex", gap:12, padding:"8px 0", alignItems:"flex-start",
                      borderTop: i === 0 ? "none" : "1px solid var(--border-card)",
                    }}>
                      <span style={{ fontSize:12, color:"var(--text-secondary)", width:94, flexShrink:0 }}>{et}</span>
                      <span style={{ fontSize:13, fontWeight:600, color:"var(--text-primary)", minWidth:0, wordBreak:"break-word" }}>{v}</span>
                    </div>
                  ))}
                </div>
                <p style={{ margin:"12px 0 0", fontSize:11.5, color:"var(--text-secondary)", lineHeight:1.5 }}>
                  Queda como solicitud hasta que la administración la apruebe. Podés
                  cancelarla desde "Mis reservas".
                </p>
              </div>

              <div style={{ display:"flex", gap:10, padding:"18px 22px 20px" }}>
                <button type="button" onClick={() => setPorConfirmar(null)} disabled={saving} style={{
                  flex:1, padding:"11px 16px", borderRadius:9,
                  border:"1px solid var(--border-input)", background:"var(--hover-bg)",
                  color:"var(--text-secondary)", cursor:saving?"not-allowed":"pointer",
                  fontSize:13.5, fontWeight:600,
                }}>
                  Volver
                </button>
                <button type="button" onClick={() => handleReservar(porConfirmar)} disabled={saving} style={{
                  flex:1, padding:"11px 16px", borderRadius:9, border:"none",
                  background:"#f97316", color:"#fff", cursor:saving?"not-allowed":"pointer",
                  fontSize:13.5, fontWeight:700, opacity:saving?0.7:1,
                  boxShadow:"0 4px 12px rgba(249,115,22,0.35)",
                }}>
                  {saving
                    ? <><i className="fa-solid fa-circle-notch fa-spin" style={{ marginRight:6 }} />Enviando…</>
                    : "Sí, reservar"}
                </button>
              </div>
            </div>
          </div>
        </ModalPortal>
      )}

      <style>{`
        @keyframes prFadeUp { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:none} }
        .pr-area-card { transition: transform .18s, box-shadow .18s !important; cursor:pointer; }
        .pr-area-card:hover { transform: translateY(-4px) !important; box-shadow: 0 12px 32px rgba(0,0,0,.18) !important; }
        .pr-area-card:hover .pr-area-overlay { opacity:1 !important; }
        .pr-area-overlay { opacity:0; transition: opacity .18s !important; }
        .pr-day-btn { transition: background .1s, color .1s !important; }
        .pr-day-btn:hover:not(:disabled) { background: rgba(249,115,22,0.15) !important; }
        .pr-horario:hover { box-shadow: 0 4px 16px rgba(0,0,0,.12) !important; }
        .pr-reserva-card { transition: box-shadow .15s !important; }
        .pr-reserva-card:hover { box-shadow: 0 6px 20px rgba(0,0,0,.12) !important; }
        @media(min-width:960px){
          .pr-layout { display:grid; grid-template-columns:1fr 1fr; gap:20px; align-items:start; }
          .pr-areas-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:18px; }
        }
        @media(min-width:600px) and (max-width:959px){
          .pr-layout { display:flex; flex-direction:column; gap:16px; }
          .pr-areas-grid { display:grid; grid-template-columns:repeat(2,1fr); gap:16px; }
        }
        @media(max-width:599px){
          .pr-layout { display:flex; flex-direction:column; gap:16px; }
          .pr-areas-grid { display:grid; grid-template-columns:1fr; gap:14px; }
        }
      `}</style>
      <div style={{ padding:"28px 28px 48px", animation:"prFadeUp .4s ease both" }}>

        {/* ── Hero ── */}
        <div style={{
          borderRadius:16, marginBottom:24, position:"relative", overflow:"hidden",
          background:heroBg, border:`1px solid ${heroBorder}`, boxShadow:heroShadow,
        }}>
          <div style={{ position:"absolute", top:-40, right:-40, width:200, height:200, borderRadius:"50%", background:`radial-gradient(circle,${isLight?"rgba(74,222,128,0.12)":"rgba(74,222,128,0.15)"} 0%,transparent 70%)`, pointerEvents:"none" }} />
          <div style={{ position:"relative", padding:"24px 28px", display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:16 }}>
            <div style={{ display:"flex", alignItems:"flex-start", gap:12 }}>
              <div style={{ width:32, height:32, borderRadius:9, flexShrink:0, background:"rgba(74,222,128,0.15)", border:"1px solid rgba(74,222,128,0.3)", display:"flex", alignItems:"center", justifyContent:"center" }}>
                <i className="fa-solid fa-calendar-check" style={{ color:"#4ade80", fontSize:14 }} />
              </div>
              <div>
                <span style={{ fontSize:13, fontWeight:700, color:"#4ade80", letterSpacing:0.5, display:"block", marginBottom:6 }}>{t("portal.reservaciones")}</span>
                <h1 style={{ fontSize:"clamp(18px,2.5vw,26px)", fontWeight:800, color:heroText, margin:"0 0 4px" }}>
                  {tab === "reservar" ? t("portal.hacerReserva") : t("portal.misReservas")}
                </h1>
                <p style={{ margin:0, fontSize:13, color:heroSub }}>
                  {tab === "reservar" ? t("portal.reservacionesDesc") : t("portal.misReservasDesc")}
                </p>
              </div>
            </div>
            {/* Cambio de pestaña. Va como control segmentado y NO como botón
                naranja suelto: con el botón relleno la gente creía que ahí se
                hacía la reserva, cuando solo cambia de vista. */}
            <div role="tablist" style={{
              display:"flex", gap:4, padding:4, borderRadius:12,
              background: isLight ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.07)",
              border:`1px solid ${isLight ? "rgba(0,0,0,0.07)" : "rgba(255,255,255,0.1)"}`,
            }}>
              {[["reservar", t("portal.tabCalendario"), "fa-calendar-days"],
                ["mis-reservas", t("portal.tabMisReservas"), "fa-list-check"]].map(([id, label, icon]) => {
                const activo = tab === id;
                return (
                  <button key={id} role="tab" aria-selected={activo} onClick={() => setTab(id)} style={{
                    padding:"8px 16px", borderRadius:9, border:"none", cursor:"pointer",
                    fontWeight:700, fontSize:13, display:"flex", alignItems:"center", gap:7,
                    background: activo ? (isLight ? "#fff" : "rgba(255,255,255,0.14)") : "transparent",
                    color: activo ? heroText : heroSub,
                    boxShadow: activo ? (isLight ? "0 1px 3px rgba(0,0,0,0.12)" : "none") : "none",
                    transition:"all .15s",
                  }}>
                    <i className={`fa-solid ${icon}`} style={{ fontSize:12 }} /> {label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* ── TAB: Reservar ── */}
        {tab === "reservar" && (
          <>
            {/* Bloqueo por mora: el condominio no deja reservar con facturas
                vencidas. Se avisa acá y no al final, para que nadie llene el
                formulario para nada. */}
            {bloqueado && (
              <div style={{
                marginBottom:20, borderRadius:14, padding:"16px 20px",
                background: isLight ? "rgba(248,113,113,0.08)" : "rgba(248,113,113,0.1)",
                border:"1px solid rgba(248,113,113,0.35)",
                display:"flex", gap:14, alignItems:"flex-start",
              }}>
                <i className="fa-solid fa-circle-exclamation" style={{ color:"#f87171", fontSize:20, marginTop:2 }} />
                <div>
                  <p style={{ margin:"0 0 4px", fontWeight:800, fontSize:14, color:"var(--text-primary)" }}>
                    Por ahora no podés reservar
                  </p>
                  <p style={{ margin:0, fontSize:13, color:"var(--text-secondary)", lineHeight:1.55 }}>
                    {reglas.mora.motivo}
                  </p>
                </div>
              </div>
            )}

            {/* Area card grid — shown when no area is selected */}
            {!selectedArea && (
              <>
                {areas.length === 0 ? (
                  <div style={{ textAlign:"center", color:"var(--text-secondary)", padding:56, background:"var(--bg-card)", borderRadius:16, border:"1px solid var(--border-card)" }}>
                    <i className="fa-solid fa-building" style={{ fontSize:40, opacity:.3, display:"block", marginBottom:12 }} />
                    <p style={{ margin:0, fontSize:14 }}>{t("portal.sinAreas")}</p>
                  </div>
                ) : (
                  <div className="pr-areas-grid">
                    {areas.map(a => (
                      <div key={a.id} className="pr-area-card" onClick={() => { setSelectedArea(a); setSelDate(null); setVerDetalles(false); }} style={{
                        background:"var(--bg-card)", border:"1px solid var(--border-card)",
                        borderRadius:16, overflow:"hidden",
                        boxShadow: isLight?"0 2px 8px rgba(0,0,0,.07)":"0 2px 12px rgba(0,0,0,.25)",
                        position:"relative",
                      }}>
                        {/* Image */}
                        <div style={{ position:"relative", width:"100%", height:160, background:"var(--hover-bg)", overflow:"hidden" }}>
                          {a.imagen_url ? (
                            <img src={a.imagen_url} alt={a.nombre} style={{ width:"100%", height:"100%", objectFit:"cover" }} />
                          ) : (
                            <div style={{ width:"100%", height:"100%", display:"flex", alignItems:"center", justifyContent:"center" }}>
                              <i className="fa-solid fa-building" style={{ fontSize:40, color:"var(--text-secondary)", opacity:.3 }} />
                            </div>
                          )}
                          {/* Hover overlay */}
                          <div className="pr-area-overlay" style={{
                            position:"absolute", inset:0,
                            background:"rgba(249,115,22,0.7)",
                            display:"flex", alignItems:"center", justifyContent:"center",
                          }}>
                            <span style={{ color:"#fff", fontWeight:800, fontSize:14, display:"flex", alignItems:"center", gap:8 }}>
                              <i className="fa-solid fa-calendar-plus" /> {t("portal.reservar")}
                            </span>
                          </div>
                        </div>
                        {/* Info */}
                        <div style={{ padding:"14px 16px 18px" }}>
                          <h3 style={{ margin:"0 0 6px", fontSize:16, fontWeight:800, color:"var(--text-primary)" }}>{a.nombre}</h3>
                          {a.descripcion && (
                            <p style={{
                              margin:0, fontSize:13, color:"var(--text-secondary)", lineHeight:1.55,
                              display:"-webkit-box", WebkitLineClamp:3, WebkitBoxOrient:"vertical", overflow:"hidden",
                            }}>{a.descripcion}</p>
                          )}
                          <div style={{ marginTop:12, display:"flex", alignItems:"center", gap:6, flexWrap:"wrap" }}>
                            <span style={{ fontSize:11, fontWeight:700, color:"#4ade80", background:"rgba(74,222,128,0.12)", border:"1px solid rgba(74,222,128,0.25)", borderRadius:20, padding:"2px 10px" }}>
                              {(a.horarios?.filter(h=>h.activo)||[]).length} {t("portal.horarios")}
                            </span>
                            {a.precio_colones && (
                              <span style={{ fontSize:11, fontWeight:700, color:"#10b981" }}>
                                ₡{Number(a.precio_colones).toLocaleString("en-US")}
                              </span>
                            )}
                            {a.precio_dolares && (
                              <span style={{ fontSize:11, fontWeight:700, color:"#3b82f6" }}>
                                ${Number(a.precio_dolares).toLocaleString("en-US", { minimumFractionDigits: 2 })}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {selectedArea && (
              <>
                {/* Back button */}
                {areas.length > 0 && (
                  <button onClick={() => { setSelectedArea(null); setSelDate(null); setVerDetalles(false); }} style={{
                    display:"inline-flex", alignItems:"center", gap:7, marginBottom:18,
                    background:"var(--hover-bg)", border:"1px solid var(--border-card)",
                    borderRadius:20, padding:"7px 16px", cursor:"pointer",
                    fontWeight:600, fontSize:13, color:"var(--text-secondary)",
                    transition:"all .15s",
                  }}>
                    <i className="fa-solid fa-arrow-left" style={{ fontSize:11 }} /> {t("portal.cambiarArea")}
                  </button>
                )}
              <div className="pr-layout">
                {/* LEFT: area info + calendar */}
                <div>
                  <div style={{ background:"var(--bg-card)", border:"1px solid var(--border-card)", borderRadius:14, overflow:"hidden", marginBottom:16, boxShadow: isLight?"0 2px 8px rgba(0,0,0,.05)":"0 2px 12px rgba(0,0,0,.2)" }}>
                    {/* `contain` y no `cover`: recortada se perdía media foto
                        del área. El fondo rellena lo que sobre a los lados. */}
                    {selectedArea.imagen_url && (
                      <a href={selectedArea.imagen_url} target="_blank" rel="noreferrer"
                        title={t("portal.verImagenCompleta")}
                        style={{ display:"block", background: isLight?"rgba(0,0,0,0.04)":"rgba(255,255,255,0.04)" }}>
                        <img src={selectedArea.imagen_url} alt={selectedArea.nombre}
                          style={{ width:"100%", height:170, objectFit:"contain", display:"block" }} />
                      </a>
                    )}
                    <div style={{ padding:"16px 18px" }}>
                      <h3 style={{ margin:"0 0 8px", fontSize:17, fontWeight:800, color:"var(--text-primary)" }}>{selectedArea.nombre}</h3>
                      {(selectedArea.precio_colones || selectedArea.precio_dolares) && (
                        <div style={{ display:"flex", gap:10, marginBottom:10, flexWrap:"wrap" }}>
                          {selectedArea.precio_colones && (
                            <span style={{ fontSize:13, fontWeight:800, color:"#10b981", background:"rgba(16,185,129,0.1)", border:"1px solid rgba(16,185,129,0.25)", borderRadius:8, padding:"3px 10px" }}>
                              ₡{Number(selectedArea.precio_colones).toLocaleString("en-US")}
                            </span>
                          )}
                          {selectedArea.precio_dolares && (
                            <span style={{ fontSize:13, fontWeight:800, color:"#3b82f6", background:"rgba(59,130,246,0.1)", border:"1px solid rgba(59,130,246,0.25)", borderRadius:8, padding:"3px 10px" }}>
                              ${Number(selectedArea.precio_dolares).toLocaleString("en-US", { minimumFractionDigits:2 })}
                            </span>
                          )}
                        </div>
                      )}
                      {selectedArea.contacto_nombre && (
                        <div style={{ display:"flex", alignItems:"center", gap:8, padding:"8px 12px", borderRadius:8, background:"rgba(249,115,22,0.08)", border:"1px solid rgba(249,115,22,0.2)" }}>
                          <i className="fa-solid fa-phone" style={{ color:"#f97316", fontSize:12 }} />
                          <span style={{ fontSize:12, color:"var(--text-primary)", fontWeight:600 }}>{selectedArea.contacto_nombre}</span>
                          <span style={{ fontSize:12, color:"var(--text-secondary)" }}>-</span>
                          <span style={{ fontSize:12, color:"var(--text-secondary)" }}>{selectedArea.contacto_telefono}</span>
                        </div>
                      )}

                      {/* Reglamento del área: plegado por defecto. Suele ser
                          un texto largo (artículos completos) y, suelto, dejaba
                          el calendario fuera de la pantalla — el residente creía
                          que el sistema se había caído. Abierto, va en una caja
                          con scroll propio para que el calendario no se mueva. */}
                      {selectedArea.descripcion && (
                        <div style={{ marginTop:10 }}>
                          <button type="button" onClick={() => setVerDetalles(v => !v)} style={{
                            display:"flex", alignItems:"center", gap:8, width:"100%",
                            background:"var(--hover-bg)", border:"1px solid var(--border-card)",
                            borderRadius:9, padding:"9px 12px", cursor:"pointer",
                            fontSize:12.5, fontWeight:700, color:"var(--text-secondary)", textAlign:"left",
                          }}>
                            <i className="fa-solid fa-circle-info" style={{ color:"#f97316", fontSize:12 }} />
                            <span style={{ flex:1 }}>{verDetalles ? t("portal.ocultarDetalles") : t("portal.verDetalles")}</span>
                            <i className={`fa-solid fa-chevron-${verDetalles ? "up" : "down"}`} style={{ fontSize:11 }} />
                          </button>
                          {verDetalles && (
                            <div style={{
                              marginTop:8, maxHeight:220, overflowY:"auto",
                              padding:"12px 14px", borderRadius:9,
                              border:"1px solid var(--border-card)", background:"var(--hover-bg)",
                              fontSize:12.5, lineHeight:1.6, color:"var(--text-secondary)",
                              whiteSpace:"pre-wrap",
                            }}>
                              {selectedArea.descripcion}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Calendar */}
                  <div style={{ background:"var(--bg-card)", border:"1px solid var(--border-card)", borderRadius:14, overflow:"hidden", boxShadow: isLight?"0 2px 8px rgba(0,0,0,.05)":"0 2px 12px rgba(0,0,0,.2)" }}>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"14px 18px", borderBottom:"1px solid var(--border-card)", background: isLight?"rgba(0,0,0,0.02)":"rgba(255,255,255,0.02)" }}>
                      <button onClick={prevMonth} style={{ background:"none", border:"none", cursor:"pointer", color:"var(--text-secondary)", padding:"4px 8px", borderRadius:6 }}>
                        <i className="fa-solid fa-chevron-left" />
                      </button>
                      <span style={{ fontWeight:700, fontSize:15, color:"var(--text-primary)" }}>{MESES[month-1]} {year}</span>
                      <button onClick={nextMonth} style={{ background:"none", border:"none", cursor:"pointer", color:"var(--text-secondary)", padding:"4px 8px", borderRadius:6 }}>
                        <i className="fa-solid fa-chevron-right" />
                      </button>
                    </div>
                    <div style={{ padding:"10px 10px 4px" }}>
                      <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)", gap:3 }}>
                        {DIAS.map(d => (
                          <div key={d} style={{ textAlign:"center", fontSize:11, color:"var(--text-secondary)", fontWeight:700, paddingBottom:6 }}>{d}</div>
                        ))}
                        {Array(firstDay).fill(null).map((_,i) => <div key={"e"+i} />)}
                        {Array.from({length:daysInMonth},(_,i) => {
                          const d=i+1, ds=dateStr(d);
                          const isPast=ds<todayStr, isToday=ds===todayStr, isSel=ds===selectedDate;
                          // Fuera del tope de anticipación: se ve igual que un
                          // día pasado, no se puede escoger.
                          const isLejos=!!fechaMaxima&&ds>fechaMaxima;
                          const anyBusy=horarios.some(h=>isOcupado(ds,h.id));
                          const allBusy=horarios.length>0&&horarios.every(h=>isOcupado(ds,h.id));
                          const inhabil=isPast||isLejos||allBusy;
                          return (
                            <button key={d} className="pr-day-btn" disabled={inhabil}
                              title={isLejos?`Solo se puede reservar hasta el ${fechaMaxima}`:undefined}
                              onClick={()=>!inhabil&&setSelDate(ds)} style={{
                              padding:"9px 2px", borderRadius:8, border:"none",
                              cursor:inhabil?"default":"pointer",
                              fontWeight:isSel||isToday?700:400, fontSize:13, position:"relative",
                              background:isSel?"#f97316":isToday?"rgba(249,115,22,0.18)":"transparent",
                              color:isSel?"#fff":isPast||isLejos?"var(--text-secondary)":allBusy?"#f87171":anyBusy?"#f59e0b":"var(--text-primary)",
                              opacity:isPast||isLejos?0.35:1,
                              boxShadow:isSel?"0 4px 10px rgba(249,115,22,0.4)":"none",
                            }}>
                              {d}
                              {anyBusy&&!allBusy&&!isSel&&(
                                <span style={{ position:"absolute", bottom:3, left:"50%", transform:"translateX(-50%)", width:4, height:4, borderRadius:"50%", background:"#f59e0b" }} />
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    <div style={{ padding:"10px 18px 14px", display:"flex", gap:16, fontSize:11, color:"var(--text-secondary)", borderTop:"1px solid var(--border-card)", marginTop:6, flexWrap:"wrap" }}>
                      <span><span style={{color:"#4ade80"}}>●</span> {t("portal.disponible")}</span>
                      <span><span style={{color:"#f59e0b"}}>●</span> {t("portal.parcial")}</span>
                      <span><span style={{color:"#f87171"}}>●</span> {t("portal.lleno")}</span>
                      {fechaMaxima && (
                        <span style={{ marginLeft:"auto" }}>
                          <i className="fa-solid fa-calendar-day" style={{ marginRight:5, opacity:.6 }} />
                          Se puede reservar hasta el {fechaMaxima}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* RIGHT: horarios */}
                <div>
                  {!selectedDate ? (
                    <div style={{ background:"var(--bg-card)", border:"1px solid var(--border-card)", borderRadius:14, padding:"40px 24px", textAlign:"center", color:"var(--text-secondary)" }}>
                      <i className="fa-solid fa-hand-pointer" style={{ fontSize:32, opacity:.35, display:"block", marginBottom:12 }} />
                      <p style={{ margin:0, fontSize:14, fontWeight:500 }}>{t("portal.seleccionaFecha")}</p>
                    </div>
                  ) : (
                    <div style={{ background:"var(--bg-card)", border:"1px solid var(--border-card)", borderRadius:14, overflow:"hidden", boxShadow: isLight?"0 2px 8px rgba(0,0,0,.05)":"0 2px 12px rgba(0,0,0,.2)" }}>
                      <div style={{ padding:"14px 18px", borderBottom:"1px solid var(--border-card)", background: isLight?"rgba(0,0,0,0.02)":"rgba(255,255,255,0.02)", display:"flex", alignItems:"center", gap:10 }}>
                        <i className="fa-solid fa-calendar-day" style={{ color:"#f97316", fontSize:13 }} />
                        <h3 style={{ margin:0, fontSize:14, fontWeight:700, color:"var(--text-primary)", textTransform:"capitalize" }}>
                          {fechaLarga(selectedDate)}
                        </h3>
                      </div>
                      {horarios.length === 0 ? (
                        <p style={{ padding:24, color:"var(--text-secondary)", textAlign:"center", margin:0 }}>{t("portal.sinHorarios")}</p>
                      ) : (
                        <div style={{ padding:14 }}>
                          {/* El orden va de lo opcional a lo que decide: tocar un
                              horario es el último paso y el que abre la
                              confirmación, así el residente ya llega con el
                              comprobante y las notas puestos. */}
                          <div>
                            <label style={{ fontSize:12, color:"var(--text-secondary)", fontWeight:700, display:"block", marginBottom:6, letterSpacing:0.5 }}>
                              {t("portal.comprobantePago") || "Comprobante de pago"} ({t("common.opcional")})
                            </label>
                            <div onClick={() => fileRef.current?.click()} style={{
                              border:`2px dashed ${comprobante?"#4ade80":"var(--border-input)"}`,
                              borderRadius:11, padding:"18px 14px", textAlign:"center", cursor:"pointer",
                              background: comprobante?"rgba(74,222,128,0.06)":"transparent",
                              transition:"all .2s",
                            }}>
                              <i className={`fa-solid ${comprobante?"fa-file-check":"fa-cloud-arrow-up"}`}
                                style={{ fontSize:20, color:comprobante?"#4ade80":"var(--text-secondary)", display:"block", marginBottom:6 }} />
                              <p style={{ margin:0, fontSize:13, fontWeight:700, color:comprobante?"#4ade80":"var(--text-primary)" }}>
                                {comprobante ? comprobante.name : (t("portal.subirComprobante") || "Subir comprobante")}
                              </p>
                              {comprobante && (
                                <button type="button" onClick={e => { e.stopPropagation(); setComprobante(null); if (fileRef.current) fileRef.current.value = ""; }} style={{
                                  marginTop:8, background:"rgba(248,113,113,0.1)", border:"1px solid rgba(248,113,113,0.3)",
                                  color:"#f87171", borderRadius:7, padding:"3px 10px", cursor:"pointer", fontSize:11, fontWeight:600,
                                }}>
                                  {t("common.limpiar")}
                                </button>
                              )}
                            </div>
                            <input ref={fileRef} type="file" accept="image/*,.pdf"
                              onChange={e => setComprobante(e.target.files[0] || null)}
                              style={{ display:"none" }} />
                          </div>

                          <div style={{ marginTop:14 }}>
                            <label style={{ fontSize:12, color:"var(--text-secondary)", fontWeight:700, display:"block", marginBottom:6, letterSpacing:0.5 }}>
                              {t("portal.notas")} ({t("common.opcional")})
                            </label>
                            <textarea value={notas} onChange={e=>setNotas(e.target.value)}
                              rows={2} placeholder={t("portal.notasPlaceholder")}
                              className="form-control" style={{ width:"100%", resize:"vertical", boxSizing:"border-box" }} />
                          </div>

                          <div style={{ marginTop:18, marginBottom:8 }}>
                            <label style={{ fontSize:12, color:"var(--text-secondary)", fontWeight:700, display:"block", letterSpacing:0.5 }}>
                              {t("portal.horarios")}
                            </label>
                            <p style={{ margin:"4px 0 0", fontSize:11.5, color:"var(--text-secondary)", lineHeight:1.5 }}>
                              Tocá el horario que querés. Antes de enviarla te vamos a pedir que confirmes.
                            </p>
                          </div>

                          {horarios.map(h => {
                            const ocupado = isOcupado(selectedDate, h.id);
                            return (
                              <div key={h.id} className="pr-horario" style={{
                                display:"flex", justifyContent:"space-between", alignItems:"center",
                                padding:"14px 16px", borderRadius:11, marginBottom:10,
                                background: ocupado ? "var(--hover-bg)" : isLight ? "rgba(74,222,128,0.07)" : "rgba(74,222,128,0.06)",
                                border:`1px solid ${ocupado?"var(--border-card)":"rgba(74,222,128,0.25)"}`,
                                transition:"box-shadow .15s",
                              }}>
                                <div>
                                  <p style={{ margin:0, fontWeight:700, fontSize:14, color:"var(--text-primary)" }}>{h.nombre}</p>
                                  <p style={{ margin:"2px 0 0", fontSize:12, color:"var(--text-secondary)" }}>
                                    <i className="fa-solid fa-clock" style={{ marginRight:4, fontSize:10 }} />{hhmm(h.hora_inicio)} - {hhmm(h.hora_fin)}
                                  </p>
                                </div>
                                {ocupado ? (
                                  <span style={{ fontSize:12, color:"#f87171", fontWeight:700, background:"rgba(248,113,113,0.1)", border:"1px solid rgba(248,113,113,0.3)", borderRadius:8, padding:"4px 10px" }}>
                                    {t("portal.ocupado")}
                                  </span>
                                ) : (
                                  <button onClick={() => setPorConfirmar(h)} disabled={saving || bloqueado}
                                    title={bloqueado ? reglas.mora.motivo : undefined} style={{
                                    background: bloqueado ? "var(--hover-bg)" : "#f97316",
                                    color: bloqueado ? "var(--text-secondary)" : "#fff",
                                    border: bloqueado ? "1px solid var(--border-card)" : "none", borderRadius:9,
                                    padding:"9px 16px", cursor:saving||bloqueado?"not-allowed":"pointer",
                                    fontWeight:700, fontSize:13, opacity:saving?.7:1,
                                    boxShadow: bloqueado ? "none" : "0 4px 12px rgba(249,115,22,0.4)",
                                    transition:"opacity .15s",
                                  }}>
                                    {saving ? "..." : t("portal.reservar")}
                                  </button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
              </>
            )}
          </>
        )}

        {/* ── TAB: Mis reservas ── */}
        {tab === "mis-reservas" && (
          <div style={{ maxWidth:640, margin:"0 auto" }}>
            {reservaciones.length === 0 ? (
              <div style={{ textAlign:"center", color:"var(--text-secondary)", padding:56, background:"var(--bg-card)", borderRadius:16, border:"1px solid var(--border-card)" }}>
                <i className="fa-solid fa-calendar-xmark" style={{ fontSize:40, opacity:.3, display:"block", marginBottom:12 }} />
                <p style={{ margin:0, fontSize:14 }}>{t("portal.sinReservas")}</p>
              </div>
            ) : (
              reservaciones.map(r => (
                <div key={r.id} className="pr-reserva-card" style={{
                  background:"var(--bg-card)", border:"1px solid var(--border-card)",
                  borderRadius:14, padding:"16px 20px", marginBottom:12,
                  boxShadow: isLight?"0 2px 8px rgba(0,0,0,.05)":"0 2px 10px rgba(0,0,0,.2)",
                }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:8 }}>
                    <div>
                      <p style={{ margin:0, fontWeight:800, fontSize:15, color:"var(--text-primary)" }}>{r.area_nombre}</p>
                      <p style={{ margin:"3px 0 0", fontSize:13, color:"var(--text-secondary)" }}>
                        <i className="fa-solid fa-clock" style={{ marginRight:5, fontSize:11 }} />{r.horario_nombre}
                        <span style={{ margin:"0 6px", opacity:.4 }}>·</span>
                        <i className="fa-solid fa-calendar" style={{ marginRight:5, fontSize:11 }} />{r.fecha}
                      </p>
                    </div>
                    <Badge estado={r.estado} />
                  </div>
                  {r.notas_admin && (
                    <div style={{ margin:"8px 0 0", padding:"8px 12px", borderRadius:8, background:"rgba(249,115,22,0.07)", border:"1px solid rgba(249,115,22,0.2)", fontSize:12, color:"var(--text-secondary)", fontStyle:"italic" }}>
                      <i className="fa-solid fa-comment" style={{ marginRight:6, color:"#f97316" }} />{r.notas_admin}
                    </div>
                  )}
                  {r.estado === "PENDIENTE" && r.vence_el && (
                    <div style={{ margin:"8px 0 0", padding:"8px 12px", borderRadius:8, background:"rgba(245,158,11,0.07)", border:"1px solid rgba(245,158,11,0.25)", fontSize:12, color:"var(--text-secondary)" }}>
                      <i className="fa-solid fa-hourglass-half" style={{ marginRight:6, color:"#f59e0b" }} />
                      Si el pago no se confirma antes del <strong style={{ color:"var(--text-primary)" }}>{r.vence_el}</strong>, esta reserva se cancela sola.
                    </div>
                  )}
                  {r.estado === "PENDIENTE" && (
                    <button onClick={() => setConfirm({ pk: r.id, name: `${r.area_nombre} - ${r.fecha}` })} style={{
                      marginTop:12, background:"rgba(248,113,113,0.1)", border:"1px solid rgba(248,113,113,0.3)",
                      color:"#f87171", borderRadius:8, padding:"7px 14px", cursor:"pointer", fontWeight:700, fontSize:12,
                    }}>
                      <i className="fa-solid fa-xmark" style={{ marginRight:5 }} />{t("portal.cancelar")}
                    </button>
                  )}
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </>
  );
}
